import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { UserAuth } from "../../utils/AuthContext";
import { useContext, useEffect, useMemo, useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import MonthlyAttendanceBar from "./MonthlyAttendanceBar";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import { useTranslation } from "react-i18next";
import BackToDashboard from "../../components/BackToDashboard";
import previousIcon from "../../assets/previous_icon.png";
import saveIcon from "../../assets/save_icon.png";
import editIcon from "../../assets/edit_icon.png";
import closeIcon from "../../assets/close_icon.png";
import nextIcon from "../../assets/next_icon.png";
import useLanguage from "../../utils/useLanguage";
import PeriodSearchFilter from "../../components/PeriodSearchFilter";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";
import CollapsibleCard, { CARD_CONTENT_TYPES } from "./CollapsibleCard";
import { toMoney } from "../../utils/toMoney";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import PayslipTemplate from "./PayslipTemplate";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import Modal from "../../components/Modal";
import {
  Radio,
  RadioGroup,
  RadioGroupItem,
  RadioLabel,
} from "../../components/Radio";
import CustomCheckbox from "../../components/CustomCheckbox";
import ErrorBox from "../../components/ErrorBox";
import { COMPANY_DATA } from "../../constants";
import EmployeeEntitlementsPage from "../school/EmployeeEntitlementsPage";
import EntitlementsCardContent from "./EntitlementsCard";
import DeductionsCardContent from "./DeductionsCard";

const EmployeeAttendancePage = () => {
  // HOOKS
  const { userData } = useContext(UserAuth);
  const { id } = useParams();
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const navigate = useNavigate();

  // STATES
  const [searchQuery, setSearchQuery] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    currentPage: 1,
    totalRecords: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingSalary, setEditingSalary] = useState(false);
  const [isEditingVacation, setIsEditingVacation] = useState(false);
  const [hourlyRate, setHourlyRate] = useState(0);
  const [amountOfHours, setAmountOfHours] = useState(0);
  const [workDaysPerMonth, setWorkingDaysInWeek] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState({
    name: "",
    value: new Date().getMonth(),
  });
  const [vacationInfo, setVacationInfo] = useState({
    vacationBalance: 0,
    usedVacation: 0,
    deservedVacations: 0,
    yearlyDeservedVacations: 0,
  });
  const [employeeInfo, setEmployeeInfo] = useState({
    username: "",
    email: "",
    phone: "",
    address: "",
  });
  const [selectedYear, setSelectedYear] = useState({
    value: new Date().getFullYear(),
    name: new Date().getFullYear().toString(),
  });
  const [selectedStatus, setSelectedStatus] = useState({
    title: t("status"),
    value: "status",
    type: "dropdown",
    dropdown: {
      data: [
        { value: "", title: t("selectAll") },
        { value: "PRESENT", title: t("present") },
        { value: "ABSENT", title: t("absent") },
        { value: "VACATION", title: t("vacation") },
        { value: "OFFDAY", title: t("offday") },
      ],
    },
  });
  const [showConfirmSalaryModal, setShowConfirmSalaryModal] = useState(false);
  const [salaryChangeType, setSalaryChangeType] = useState("future-days");
  const [includePaidMonths, setIncludePaidMonths] = useState(false);
  const [includeUnpaidMonths, setIncludeUnpaidMonths] = useState(false);
  const [changeHourlyRateError, setChangeHourlyRateError] = useState("");
  const [updatingHourlyRateLoading, setUpdatingHourlyRateLoading] =
    useState(false);
  const [updatingYearlyDeservedVacations, setUpdatingYearlyDeservedVacations] =
    useState(false);
  const [
    updateYearlyDeservedVacationsError,
    setUpdateYearlyDeservedVacationsError,
  ] = useState("");
  const [
    fetchingEmployeeAttendanceDetailsError,
    setFetchingEmployeeAttendanceDetailsError,
  ] = useState("");

  // MEMOIZED VALUES
  const searchTitles = useMemo(
    () => [
      {
        title: t("status"),
        value: "status",
        type: "dropdown",
        dropdown: {
          data: [
            { value: "", title: t("selectAll") },
            { value: "PRESENT", title: t("present") },
            { value: "ABSENT", title: t("absent") },
            { value: "VACATION", title: t("vacation") },
            { value: "OFFDAY", title: t("offday") },
          ],
        },
      },
    ],
    [t]
  );

  const salaryData = [
    {
      type: CARD_CONTENT_TYPES.EDITABLE,
      title: t("hourlyRate"),
      value: `${toMoney(hourlyRate)} ${t("IQD")}`,
      element: (
        <div className="o-row o-row--vertical-align_center">
          <p className="u-width-specific_150">{t("hourlyRate")}: </p>
          <CustomInputWithUnit
            type="moneyV2"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            unit={t("IQD")}
          />
        </div>
      ),
    },
    {
      type: CARD_CONTENT_TYPES.EDITABLE,
      title: t("workingHoursInDay"),
      value: `${amountOfHours} ${t("hour")}`,
      element: (
        <div className="o-row o-row--vertical-align_center">
          <p className="u-width-specific_150">{t("workingHoursInDay")}: </p>
          <CustomInputWithUnit
            type="moneyV2"
            value={amountOfHours}
            onChange={(e) => setAmountOfHours(e.target.value)}
            unit={t("hour")}
          />
        </div>
      ),
    },
    {
      type: CARD_CONTENT_TYPES.EDITABLE,
      title: t("workDaysPerMonth"),
      value: `${workDaysPerMonth} ${t("daySingular")}`,
      element: (
        <div className="o-row o-row--vertical-align_center">
          <p className="u-width-specific_150">{t("workDaysPerMonth")}: </p>
          <CustomInputWithUnit
            type="moneyV2"
            value={workDaysPerMonth}
            onChange={(e) => setWorkingDaysInWeek(e.target.value)}
            unit={t("day")}
          />
        </div>
      ),
    },
    {
      type: CARD_CONTENT_TYPES.TEXT,
      title: t("dailySalary"),
      // value: toMoney(attendanceData[0]?.baseSalary) + ` ${t("IQD")} `,
      value: toMoney(hourlyRate * amountOfHours) + ` ${t("IQD")} `,
    },
    {
      type: CARD_CONTENT_TYPES.TEXT,
      title: t("currentBalance"),
      value: toMoney(employeeInfo?.expectedUnpaidSalary || 0) + ` ${t("IQD")}`,
    },
  ];

  const vacationData = [
    {
      type: CARD_CONTENT_TYPES.EDITABLE,
      title: t("vacationBalance"),
      value: vacationInfo.vacationBalance + " " + t("daySingular"),
      element: (
        <div className="o-row o-row--vertical-align_center">
          <p className="u-width-specific_150">{t("vacationBalance")}: </p>
          <CustomInputWithUnit
            value={vacationInfo.vacationBalance}
            onChange={(e) =>
              setVacationInfo({
                ...vacationInfo,
                vacationBalance: parseFloat(e.target.value) || 0,
              })
            }
            unit={t("daySingular")}
          />
        </div>
      ),
    },
    {
      type: CARD_CONTENT_TYPES.TEXT,
      title: t("remainingVacations"),
      value:
        vacationInfo.vacationBalance -
        vacationInfo.usedVacation +
        " " +
        t("daySingular"),
    },
    {
      type: CARD_CONTENT_TYPES.TEXT,
      title: t("usedVacations"),
      value: vacationInfo.usedVacation + " " + t("daySingular"),
    },
    {
      type: CARD_CONTENT_TYPES.TEXT,
      title: t("monthlyDeservedVacations"),
      value: vacationInfo.deservedVacations.toFixed(2) + " " + t("daySingular"),
    },
  ];

  // API FUNCTIONS
  const getUserVacationInfo = async () => {
    const token = localStorage.getItem("token");
    const organizationId = localStorage.getItem("organizationId");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/employeeVacations`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": organizationId,
          },
          params: { userId: id },
        }
      );

      if (response.status == 200) {
        const data = response.data;
        setVacationInfo({
          vacationBalance: data.vacationBalance || 0,
          usedVacation: data.usedVacation || 0,
          deservedVacations: data.deservedVacations || 0,
          yearlyDeservedVacations: data.yearlyDeservedVacations || 0,
        });
      }
    } catch (error) {
      console.error(
        "Failed to fetch vacation info:",
        error.response?.data || error.message
      );
    }
  };

  const fetchEmployeeAttendanceDetails = async () => {
    const token = localStorage.getItem("token");
    try {
      setFetchingEmployeeAttendanceDetailsError("");
      setIsLoading(true);

      const params = {
        page: pagination.currentPage,
        limit: 2,
        organizationId: parseInt(userData.organizationId),
      };

      if (selectedMonth) params.month = parseInt(selectedMonth.value) || null;
      if (selectedYear) params.year = parseInt(selectedYear.value);
      if (selectedStatus?.value) params.status = searchQuery.value;
      if (includePaidMonths) params.paidStatus = "paid";
      if (includeUnpaidMonths) params.paidStatus = "unpaid";
      if (includePaidMonths && includeUnpaidMonths) params.paidStatus = "both";

      const res = await axios.get(
        `${process.env.REACT_APP_URL}/api/attendance/getEmployeeAttendance/${id}`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 200) {
        setAttendanceData([
          {
            userId: id,
            username: res.data.username,
            hourlySalary: res.data.hourlySalary,
            baseSalary: res.data.baseMonthlySalary,
            organizationId: res.data.organizationId,
            data: res.data.data,
          },
        ]);

        setEmployeeInfo({
          username: res.data.username,
          email: res.data.email,
          phone: res.data.phoneNumber,
          address: res.data.address,
          expectedUnpaidSalary: res.data.expectedUnpaidSalary,
          workDaysPerMonth: res.data.workDaysPerMonth,
        });

        setAmountOfHours(res.data.workingHours);
        setWorkingDaysInWeek(res.data.workDaysPerMonth || 0);

        setHourlyRate(res.data.hourlySalary);
        setPagination({
          totalPages: res.data.pagination.totalPages,
          currentPage: res.data.pagination.currentPage,
          totalRecords: res.data.pagination.totalRecords,
        });
      }
    } catch (error) {
      console.error("Error fetching employee attendance details:", error);
      setFetchingEmployeeAttendanceDetailsError(
        "errorFetchingEmployeeAttendanceDetails"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateHourlyRate = async (newHourlyRate) => {
    setUpdatingHourlyRateLoading(true);
    const organizationId = localStorage.getItem("organizationId");
    const token = localStorage.getItem("token");
    try {
      if (salaryChangeType === "both") {
        const response = await axios.patch(
          `${process.env.REACT_APP_URL}/api/attendance/updateEmployeeAttendance`,
          {
            userId: parseInt(id),
            hourlySalary: parseFloat(newHourlyRate),
            workingHours: sanitizeNumber(amountOfHours),
            workDaysPerMonth,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              organizationid: organizationId,
            },
          }
        );

        if (response.status === 200) {
          setHourlyRate(newHourlyRate);
          setEditingSalary(false);
          fetchEmployeeAttendanceDetails();
          setShowConfirmSalaryModal(false);
          setSalaryChangeType("future-days");
        }

        return;
      }
      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/editUser`,
        {
          id: parseInt(id),
          hourlySalary: parseFloat(newHourlyRate),
          workingHours: sanitizeNumber(amountOfHours),
          workDaysPerMonth,
        }
      );

      if (response.status === 200) {
        setHourlyRate(newHourlyRate);
        setEditingSalary(false);
        fetchEmployeeAttendanceDetails();
        setShowConfirmSalaryModal(false);
        setSalaryChangeType("future-days");
      }
    } catch (error) {
      console.log("error", error);
      if (
        error.response?.data.message ===
        "youDon'tHavePermissionToUpdateEmployeeAttendance"
      ) {
        setChangeHourlyRateError(
          "youDon'tHavePermissionToUpdateEmployeeAttendance"
        );
        return;
      }
      setChangeHourlyRateError("errorUpdatingEmployeeAttendance");
      console.error("Error updating hourly rate:", error);
    } finally {
      setUpdatingHourlyRateLoading(false);
    }
  };

  const updateYearlyDeservedVacations = async () => {
    try {
      setUpdatingYearlyDeservedVacations(true);
      setUpdateYearlyDeservedVacationsError("");
      const res = await axios.patch(
        `${process.env.REACT_APP_URL}/employeeVacations`,
        {
          id: parseInt(id),
          days: vacationInfo.vacationBalance,
        }
      );
      setIsEditingVacation(false);
      return res.data;
    } catch (error) {
      console.error(
        "Error updating yearly vacation days:",
        error.response?.data || error.message
      );
      setUpdateYearlyDeservedVacationsError(
        "errorUpdatingYearlyDeservedVacations"
      );
    } finally {
      setUpdatingYearlyDeservedVacations(false);
    }
  };

  // HANDLERS
  const handleSearch = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    fetchEmployeeAttendanceDetails();
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: newPage,
    }));
  };

  // EFFECTS
  useEffect(() => {
    fetchEmployeeAttendanceDetails();
  }, [pagination.currentPage]);

  useEffect(() => {
    getUserVacationInfo();
  }, []);

  return (
    <PageWrapper>
      {showConfirmSalaryModal && (
        <Modal
          title="editSalary"
          onClose={() => setShowConfirmSalaryModal(false)}
          open={showConfirmSalaryModal}
          error={t(changeHourlyRateError)}
          footer={
            <section className="edit-user-actions">
              <CustomButton
                title={isLoading ? t("saving") : t("confirm")}
                icon={saveIcon}
                iconSize={16}
                onClick={() => {
                  updateHourlyRate(hourlyRate);
                }}
              />
              <CustomButton
                title={t("cancel")}
                variant="blank"
                onClick={() => {
                  setEditingSalary(false);
                  setShowConfirmSalaryModal(false);
                  setSalaryChangeType("future-days");
                }}
                icon={closeIcon}
                iconSize={14}
              />
            </section>
          }
        >
          {changeHourlyRateError && (
            <ErrorBox>{t(changeHourlyRateError)}</ErrorBox>
          )}
          <p style={{ margin: 0 }}>{t("changeSalaryModleDescription")}</p>

          <RadioGroup>
            <RadioGroupItem>
              <Radio
                id="option1"
                name="salary-change"
                value="future-days"
                checked={salaryChangeType === "future-days"}
                onChange={() => {
                  setSalaryChangeType("future-days");
                }}
              />
              <RadioLabel htmlFor="option1">
                {t("changeaJustFutureDays")}
              </RadioLabel>
            </RadioGroupItem>

            <RadioGroupItem>
              <Radio
                id="option2"
                name="salary-change"
                value="both"
                checked={salaryChangeType === "both"}
                onChange={() => {
                  setSalaryChangeType("both");
                }}
              />
              <RadioLabel htmlFor="option2">
                {t("changePreviousAndFutureDays")}
              </RadioLabel>
            </RadioGroupItem>
          </RadioGroup>

          {updatingHourlyRateLoading && <Loader size={LOADER_SIZES.MEDIUM} />}
        </Modal>
      )}

      <div className="u-padding--bottom_500 o-column o-column--gap-size_200">
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
            onClick={() => navigate(-1)}
          />
        </div>

        <div>
          <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_600">
            <span>
              {userData?.userType === "organization"
                ? `${t("organization")}:`
                : `${t("user")}:`}
            </span>
            <span>{`${localStorage.getItem("username")}`}</span>
          </div>

          <h1 className="t-headline-1">
            {t("employeeAttendance")}: {attendanceData[0]?.username}
          </h1>
        </div>

        {isEditingSalary && !userData.specialOrg && (
          <InformationBox>
            <span className="t-bold_bold">{t("note")}: </span>
            {t("editHourlyRateInfo")}
          </InformationBox>
        )}

        {/* Employee Salary and Vacations */}
        <section className="o-column-row-switcher o-row o-row--gap-size_400 u-margin--bottom_600">
          <div className="u-width_full-width ">
            <CollapsibleCard
              title={t("salary")}
              content={salaryData}
              footerContent={
                <>
                  {isEditingSalary &&
                    !showConfirmSalaryModal &&
                    changeHourlyRateError && (
                      <ErrorBox>{t(changeHourlyRateError)}</ErrorBox>
                    )}
                  {isEditingSalary &&
                    !showConfirmSalaryModal &&
                    updatingHourlyRateLoading && (
                      <Loader size={LOADER_SIZES.MEDIUM} />
                    )}
                  <div className="o-row o-row--horizontal-align_end">
                    {isEditingSalary ? (
                      <section className="edit-user-actions">
                        <CustomButton
                          title={t("save")}
                          variant="primary"
                          onClick={() => {
                            if (!userData.specialOrg) {
                              updateHourlyRate(hourlyRate);
                              return;
                            }
                            setShowConfirmSalaryModal(true);
                          }}
                          icon={saveIcon}
                          iconSize={14}
                        />
                        <CustomButton
                          title={t("cancel")}
                          variant="blank"
                          onClick={() => {
                            setEditingSalary(false);
                          }}
                          icon={closeIcon}
                          iconSize={14}
                        />
                      </section>
                    ) : (
                      <CustomButton
                        title={t("edit")}
                        variant="blank"
                        onClick={() => {
                          setEditingSalary(true);
                          setChangeHourlyRateError("");
                        }}
                        icon={editIcon}
                        iconSize={14}
                      />
                    )}
                  </div>
                </>
              }
              isEditing={isEditingSalary}
            />
          </div>

          <div className="u-width_full-width ">
            <CollapsibleCard
              title={t("vacations")}
              content={vacationData}
              footerContent={
                <>
                  {updateYearlyDeservedVacationsError && (
                    <ErrorBox>{t(updateYearlyDeservedVacationsError)}</ErrorBox>
                  )}
                  {updatingYearlyDeservedVacations && (
                    <Loader size={LOADER_SIZES.MEDIUM} />
                  )}
                  <div className="o-row o-row--horizontal-align_end">
                    {isEditingVacation ? (
                      <section className="edit-user-actions">
                        <CustomButton
                          title={t("save")}
                          variant="primary"
                          onClick={() => {
                            updateYearlyDeservedVacations();
                          }}
                          icon={saveIcon}
                          iconSize={14}
                        />
                        <CustomButton
                          title={t("cancel")}
                          variant="blank"
                          onClick={() => {
                            setIsEditingVacation(false);
                            setUpdateYearlyDeservedVacationsError("");
                          }}
                          icon={closeIcon}
                          iconSize={14}
                        />
                      </section>
                    ) : (
                      <CustomButton
                        title={t("edit")}
                        variant="blank"
                        onClick={() => setIsEditingVacation(true)}
                        icon={editIcon}
                        iconSize={14}
                      />
                    )}
                  </div>
                </>
              }
              isEditing={isEditingVacation}
            />
          </div>
        </section>

        {/* Employee Entitltiments */}
        <section className="o-column-row-switcher o-row o-row--gap-size_400 u-margin--bottom_600">
          <CollapsibleCard
            title={t("entitlements")}
            body={<EntitlementsCardContent employeeId={id} />}
            isEditing={false}
          />
        </section>

        {/* Employee Deductions */}
        <section className="o-column-row-switcher o-row o-row--gap-size_400 u-margin--bottom_600">
          <CollapsibleCard
            title={t("deductions")}
            body={<DeductionsCardContent employeeId={id} />}
            isEditing={false}
          />
        </section>

        <div className="o-column o-column--gap-size_200">
          <div className="u-margin u-margin--bottom_500">
            <PeriodSearchFilter
              options={searchTitles}
              selected={selectedStatus}
              setSelected={setSelectedStatus}
              setSearchQuery={setSearchQuery}
              searchQuery={searchQuery}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              handleSearch={handleSearch}
              withoutFilterButton={true}
            />
            <div className="o-column u-margin--top_500">
              <div className="o-row o-row--vertical-align_center">
                <CustomCheckbox
                  checked={includePaidMonths}
                  onClick={() => setIncludePaidMonths((prev) => !prev)}
                />
                <span>{t("paidMonths")}</span>
              </div>

              <div className="o-row o-row--vertical-align_center">
                <CustomCheckbox
                  checked={includeUnpaidMonths}
                  onClick={() => setIncludeUnpaidMonths((prev) => !prev)}
                />
                <span>{t("unpaidMonths")}</span>
              </div>
            </div>
          </div>

          {fetchingEmployeeAttendanceDetailsError && (
            <ErrorBox>{t(fetchingEmployeeAttendanceDetailsError)}</ErrorBox>
          )}

          {isLoading && <Loader />}

          {!isLoading &&
            !fetchingEmployeeAttendanceDetailsError &&
            attendanceData[0]?.data?.length == 0 && (
              <InformationBox>{t("notAttendance")}</InformationBox>
            )}

          {!isLoading &&
            !fetchingEmployeeAttendanceDetailsError &&
            attendanceData[0]?.data?.length > 0 && (
              <MonthlyAttendanceBar
                data={attendanceData}
                pagination={pagination}
                employee={employeeInfo}
                onPageChange={handlePageChange}
                totalSalaryInMonth={
                  workDaysPerMonth * amountOfHours * hourlyRate
                }
              />
            )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default EmployeeAttendancePage;
